// Mini map for a drive or tree, tappable to open Google Maps directions.
//
// The map comes from the Google Maps link the admin pasted. That link can't be
// put in an iframe directly, so the backend resolves it once at save time into
// `mapsQuery` — "lat,lng", or a place name when the link carries no
// coordinates — and that is what the embed is built from. `location` is the
// written address shown as the caption underneath.
//
// If a drive has an address but no usable link, the address still gets a map,
// so nothing that worked before stops working.
//
// No API key needed. The whole preview is a real, always-visible tap target
// (not hover-only) so it behaves the same on phones as on desktop.

import { MapPin, Navigation } from "lucide-react";

/** Only ever emit http(s) links. Defence in depth: the API already rejects
 *  other schemes, but this value ends up in an href on the public site. */
function safeHttpUrl(url) {
    const v = (url || "").trim();
    if (!v) return "";
    try {
        const u = new URL(v);
        return u.protocol === "http:" || u.protocol === "https:" ? v : "";
    } catch {
        return "";
    }
}

/** The `q` the embed and the directions link are built from. */
export function mapQueryFor({ mapsQuery, location } = {}) {
    return (mapsQuery || "").trim() || (location || "").trim();
}

export default function LocationMap({
    location,
    mapsUrl,
    mapsQuery,
    compact = false,
    className = "",
}) {
    const query = mapQueryFor({ mapsQuery, location });
    if (!query) return null;

    const label = (location || "").trim() || query;
    const q = encodeURIComponent(query);
    const embedSrc = `https://maps.google.com/maps?q=${q}&z=16&output=embed`;
    // Directions prefer the exact link the admin pasted; the query is the
    // fallback for drives saved before links were the source of the map.
    const directionsHref =
        safeHttpUrl(mapsUrl) || `https://www.google.com/maps/dir/?api=1&destination=${q}`;

    return (
        <div className={`card-base bg-white overflow-hidden ${className}`}>
            <div className={`relative w-full bg-seedh-surface-beige ${compact ? "h-32" : "h-48"}`}>
                <iframe
                    title={`Map of ${label}`}
                    src={embedSrc}
                    className="absolute inset-0 w-full h-full border-0"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    tabIndex={-1}
                    aria-hidden="true"
                />
                {/* Transparent, always-visible tap target over the whole preview. */}
                <a
                    href={directionsHref}
                    target="_blank"
                    rel="noreferrer"
                    className="absolute inset-0"
                    aria-label={`Get directions to ${label}`}
                />
            </div>

            <div className="p-4 border-t border-seedh-border/70">
                <span className="flex items-start gap-2 text-sm text-seedh-text">
                    <MapPin className="w-4 h-4 text-seedh-green shrink-0 mt-0.5" />
                    <span className="leading-snug">{label}</span>
                </span>
                <a
                    href={directionsHref}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-3 w-full inline-flex items-center justify-center gap-1.5 text-xs font-medium rounded-full border border-seedh-border px-3 py-2 text-seedh-blue hover:bg-seedh-surface-blue transition-colors"
                >
                    <Navigation className="w-3.5 h-3.5" /> Get directions
                </a>
            </div>
        </div>
    );
}
